import React, { useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setReward, setStep } from "../../reducers/userReducer";
import { useNavigate } from "react-router-dom";
import Paths from "../../router/Paths";
import customerVerifyCode from "../../controllers/account/customerVerifyCode";
import resendVerification from "../../controllers/account/resendVerification";
import { enqueueSnackbar } from "notistack";

const DIGITS = 4;

export default function ClaimOTPSection() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const phone = useSelector((s) => s.user.basic.phoneNumber);
  const user = useSelector((s) => s.user.user);
  const [vals, setVals] = useState(Array(DIGITS).fill(""));
  const inputsRef = useRef([]);

  const code = useMemo(() => vals.join(""), [vals]);
  const ready = code.length === DIGITS && vals.every((v) => v !== "");
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);

  const focusAt = (i) => inputsRef.current[i]?.focus();

  const onChange = (i, v) => {
    // ✅ Allow only alphanumeric & convert to UPPERCASE
    const only = v
      .replace(/[^A-Za-z0-9]/g, "")
      .toUpperCase()
      .slice(0, 1);
    setVals((prev) => {
      const next = [...prev];
      next[i] = only;
      return next;
    });
    if (only && i < DIGITS - 1) focusAt(i + 1);
  };

  const onKeyDown = (i, e) => {
    if (e.key === "Backspace") {
      if (vals[i]) {
        setVals((prev) => {
          const next = [...prev];
          next[i] = "";
          return next;
        });
      } else if (i > 0) {
        focusAt(i - 1);
        setVals((prev) => {
          const next = [...prev];
          next[i - 1] = "";
          return next;
        });
      }
    } else if (e.key === "ArrowLeft" && i > 0) {
      e.preventDefault();
      focusAt(i - 1);
    } else if (e.key === "ArrowRight" && i < DIGITS - 1) {
      e.preventDefault();
      focusAt(i + 1);
    }
  };

  const onPaste = (i, e) => {
    e.preventDefault();
    // ✅ Allow only alphanumeric & convert to UPPERCASE
    const text = (e.clipboardData.getData("text") || "")
      .replace(/[^A-Za-z0-9]/g, "")
      .toUpperCase();
    if (!text) return;
    const chars = text.slice(0, DIGITS - i).split("");
    setVals((prev) => {
      const next = [...prev];
      chars.forEach((c, idx) => (next[i + idx] = c));
      return next;
    });
    const targetIdx = Math.min(i + chars.length, DIGITS - 1);
    focusAt(targetIdx);
  };

  const resend = async () => {
    setResending(true);
    const res = await resendVerification(user?._id).finally(() => {
      setResending(false);
    });

    if (res?.return) {
      enqueueSnackbar("Gift code resent successfully!", { variant: "success" });
    } else {
      enqueueSnackbar(
        res?.msg || "Something went wrong. Please try again later.",
        { variant: "error" }
      );
    }
  };

  const changeNumber = () => {
    dispatch(setStep(1));
  };

  const onContinue = async () => {
    if (!ready) return;
    setVerifying(true);
    const res = await customerVerifyCode(code).finally(() => {
      setVerifying(false);
    });
    const reward = res?.data?.data;
    if (reward) {
      dispatch(setReward(reward));
      navigate(Paths.congra);
    } else {
      enqueueSnackbar(
        res?.msg || "Something went wrong. Please try again later.",
        { variant: "error" }
      );
    }
  };

  return (
    <section className="w-full  py-6">
      <h2 className="text-2xl font-extrabold text-center">Gift code</h2>
      <p className="mt-2 text-center text-gray-600">
        Enter gift code sent to{" "}
        <span className="font-medium">{phone || "••••••••••"}</span>
      </p>

      {/* Boxes */}
      <div className="mt-6 grid grid-cols-4 gap-3">
        {Array.from({ length: DIGITS }).map((_, i) => (
          <input
            key={i}
            ref={(el) => (inputsRef.current[i] = el)}
            inputMode="text"
            pattern="[A-Za-z0-9]*"
            maxLength={1}
            value={vals[i]}
            onChange={(e) => onChange(i, e.target.value)}
            onKeyDown={(e) => onKeyDown(i, e)}
            onPaste={(e) => onPaste(i, e)}
            className="h-20 rounded-md border border-gray-300 bg-gray-100 text-center text-2xl uppercase
                       focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
          />
        ))}
      </div>

      {/* Links */}
      <div className="mt-6 space-y-2 text-center text-gray-600">
        <div>
          Didn’t get Gift Code?{" "}
          <button
            type="button"
            onClick={resend}
            disabled={resending}
            className="text-blue-600 underline underline-offset-2"
          >
            {resending ? <div className="primaryLoader"></div> : <>Resend</>}
          </button>
        </div>
        <div>
          Number not correct?{" "}
          <button
            type="button"
            onClick={changeNumber}
            className="text-blue-600 underline underline-offset-2"
          >
            Change
          </button>
        </div>
      </div>

      {/* CTA */}
      <div className="mt-6">
        <button
          type="button"
          onClick={onContinue}
          disabled={!ready || verifying}
          className={`w-full rounded-md py-3 text-white font-semibold shadow-sm flex items-center justify-center active:scale-[.99]
            bg-gradient-to-r from-blue-500 to-blue-600
            ${!ready ? "opacity-60 cursor-not-allowed" : ""}`}
        >
          {verifying ? <div className="buttonLoader"></div> : <>Continue</>}
        </button>
      </div>
    </section>
  );
}
